import { Close, Info } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { REQUIRED_RED } from '../App';
import { USER_TYPES } from '../config/dataService/constants';
import { MuiDisabledTextField, MuiSelectWithFocus, MuiTextFieldWithFocus } from '../config/MaterialUIConstants';
import { checkFleetNumberExists } from '../redux/Vehicles/action';
import {
    coreMasterKeys,
    useCreateVehicleMake,
    useCreateVehicleModel,
    useCreateVehicleTrim,
    useDecodeLicense,
    useDecodeVIN,
    useGetProvinceMasterList,
    useGetVehicleMakeList,
    useGetVehicleModelList,
    useGetVehicleTrimList,
    useGetVehicleYearMasterList,
} from '../services/core/master/master.hooks';
import { useGetShopConfig } from '../services/core/settings';
import { useCreateImportedVehicle, useCreateVehicle, useUpdateImportedVehicle, useUpdateVehicle } from '../services/core/vehicles/vehicle.hooks';
import { masterQueryOptions } from '../utility/Constant';
import { CustomPaper } from '../utility/SelectDropdown';
import useDebounce from '../utility/useDebounce';
import { checkValidVIN } from '../utility/utilityFunctions';
import LicensePlateInputV4 from './LicensePlateInputV4';

const STYLES = {
    dialogHeaderStyles: {
        padding: '16px!important',
        color: 'white',
        height: '64px',
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px solid #0000001F',
        background: 'rgb(0, 35, 148)',
    },
    dialogContentStyle: {
        padding: '24px!important',
        scrollbarColor: 'blue',
        scrollbarWidth: 'thin',
        overflow: 'auto',
        maxHeight: '60vh',
        maxWidth: '720px',
    },

    divider: {
        width: '100%',
        paddingLeft: '24px',
        marginTop: '24px',
    },
    requiredAsterisk: { color: '#D32F2F' },
    loaderSmall: { width: '14px', height: 'auto' },
    loaderMedium: { width: '24px', height: 'auto', marginRight: '20px' },
    inReviewText: {
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontStyle: 'italic',
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '0.15px',
        lineHeight: '120%',
    },
    trimOption: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start!important',
        justifyContent: 'start!important',
    },
};

const VehicleModalV4 = ({
    open,
    onClose,
    showMessage = false,
    isFleetCustomer = false,
    customerId = null,
    vehicleId = null,
    mode = 'create', // 'create' | 'edit' | 'confirm' | 'bulk' | 'create_from_page' | 'update_from_page' | 'update_shop_created_vehicle' | 'update_shop_imported_vehicle'
    vehicleData = null,
    onSubmit = null,
    onSuccess = null,
    onDelete = null,
    enableVINDecoding = true,
    enableLicenseDecoding = true,
    enableModelCreation = true,
    showEngineDetails = false,
    localVehicles = null,
    setLocalVehicles = null,
}) => {
    const {
        handleSubmit,
        formState: { errors },
        control,
        getValues,
        setValue,
        clearErrors,
        watch,
        setFocus,
        reset,
        setError,
    } = useForm();

    const [makeString, setMakeString] = useState('');
    const [modelString, setModelString] = useState('');
    const [trimString, setTrimString] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [trimEngineValue, setTrimEngineValue] = useState(null);
    const [decode, setDecode] = useState({
        vin: false,
        license: false,
    });

    const [yearHookValue, setyearHookValue] = useState(null);
    const [makeHookValue, setMakeHookValue] = useState(null);

    // Refs for decode flags (no re-renders needed)
    const shouldDecodeVIN = useRef(false);
    const shouldDecodeLicense = useRef(false);
    const isDecodedMake = useRef(false);
    const isDecodedModel = useRef(false);
    const isDecodedTrim = useRef(false);

    // Option the user has moved to with the arrow keys (or is hovering). Enter must select
    // this rather than fall through to "create new" just because the typed text is a partial match.
    const highlightedMake = useRef(null);
    const highlightedModel = useRef(null);
    const highlightedTrim = useRef(null);
    const queryClient = useQueryClient();
    const debounceRef = useRef(null);

    const decodeVINMutation = useDecodeVIN();
    const decodeLicenseMutation = useDecodeLicense();

    // Watch values for dependencies
    const watchYear = watch('year');
    const watchMake = watch('vehicleMakeId');
    const watchModel = watch('vehicleModelId');
    const watchVin = watch('vin');
    const watchLicense = watch('licenseNumber');
    const watchProvince = watch('provinceId');

    // Debounced values for decoding
    const debouncedVinValue = useDebounce(watchVin, 1500);
    const debouncedLicenseValue = useDebounce(watchLicense, 1500);

    // Get shop config
    const { data: config, isLoading: isConfigLoading, error: configError } = useGetShopConfig();
    const { data: provinceCombinedList, isLoading: provinceCombinedListLoader } = useGetProvinceMasterList({}, { ...masterQueryOptions });
    const { data: vehicleYearList, isLoading: vehicleYearListLoader } = useGetVehicleYearMasterList({
        page: 1,
        limit: 1000,
    });

    // Make API - only enabled when year is selected
    const { data: vehicleMakeList, isLoading: vehicleMakeListLoader } = useGetVehicleMakeList(
        { page: 1, limit: 1000, year: watchYear },
        { enabled: !!watchYear }
    );

    // Model API - only enabled when both year and make are selected
    const { data: vehicleModelList, isLoading: vehicleModelListLoader } = useGetVehicleModelList(
        { page: 1, limit: 1000, vehicleMakeId: watchMake, year: watchYear },
        { enabled: !!watchMake && !!watchYear }
    );

    // Trim API - only enabled when year, make, and model are selected
    const { data: vehicleModelTrimList, isLoading: vehicleModelTrimListLoader } = useGetVehicleTrimList(
        { page: 1, limit: 1000, vehicleModel: watchModel, vehicleMake: watchMake, year: watchYear },
        { enabled: !!watchModel && !!watchMake && !!watchYear }
    );

    // TanStack Query mutations
    const createMakeMutation = useCreateVehicleMake({
        onSuccess: (data) => {
            queryClient.refetchQueries({
                queryKey: coreMasterKeys.vehicleMakes({
                    limit: 1000,
                    page: 1,
                    year: watchYear,
                }),
            });
            // Set form values after successful creation
            if (data) {
                setValue('vehicleMakeId', data?._id);
                clearErrors('vehicleMakeId');
                setMakeHookValue(data?._id);
                setMakeString(data?.name);
                // Clear dependent fields
                setValue('vehicleModelId', '');
                setValue('vehicleModelTrimId', '');
                setModelString('');
                setTrimString('');
            }
        },
    });

    const createModelMutation = useCreateVehicleModel({
        onSuccess: (data) => {
            queryClient.refetchQueries({
                queryKey: coreMasterKeys.vehicleModels({
                    limit: 1000,
                    page: 1,
                    vehicleMakeId: watchMake,
                    year: watchYear,
                }),
            });
            // Set form values after successful creation
            if (data) {
                setValue('vehicleModelId', data?._id);
                clearErrors('vehicleModelId');
                setModelString(data?.name);
                setValue('vehicleModelTrimId', '');
                setTrimString('');
            }
        },
    });
    const createTrimMutation = useCreateVehicleTrim({
        onSuccess: (data) => {
            // Set form values after successful creation
            queryClient.refetchQueries({
                queryKey: coreMasterKeys.vehicleTrims({
                    limit: 1000,
                    page: 1,
                    vehicleMake: watchMake,
                    vehicleModel: watchModel,
                    year: watchYear,
                }),
            });
            if (data) {
                setValue('vehicleModelTrimId', data?._id);
                setTrimEngineValue(data);
            }
        },
    });

    // Vehicle CRUD mutations
    const createVehicleMutation = useCreateVehicle({
        onSuccess: (data) => {
            if (onSuccess) {
                onSuccess(data?.data);
            }
            handleClose();
        },
    });

    const updateVehicleMutation = useUpdateVehicle({
        onSuccess: (data) => {
            if (onSuccess) {
                onSuccess(data?.data);
            }
            handleClose();
        },
    });

    const createImportedVehicleMutation = useCreateImportedVehicle({
        onSuccess: (data) => {
            if (onSuccess) {
                onSuccess(data?.data);
            }
            handleClose();
        },
    });

    const updateImportedVehicleMutation = useUpdateImportedVehicle({
        onSuccess: (data) => {
            if (onSuccess) {
                onSuccess(data?.data);
            }
            handleClose();
        },
    });

    // VIN Decoding
    const handleDecodeVIN = async () => {
        if (getValues()?.vin && getValues()?.vin.length === 17) {
            clearErrors('vin');

            try {
                const resp = await decodeVINMutation.mutateAsync(getValues()?.vin);

                if (resp?.status) {
                    setValue('year', resp?.data?.year + '');
                    setyearHookValue(resp?.data?.year + '');
                    setValue('vehicleMakeId', resp?.data?.make?._id);
                    setMakeHookValue(resp?.data?.make?._id);
                    setMakeString(resp?.data?.make?.name || '');
                    setValue('vehicleModelId', resp?.data?.model?._id);
                    setValue('vehicleModelTrimId', resp?.data?.trim?._id);
                    setModelString(resp?.data?.model?.name || '');
                    setTrimString(resp?.data?.trim?.name || '');

                    // Set flags for decoded values so auto-creation runs on modal submit if unsaved
                    isDecodedMake.current = !!resp?.data?.make?.name;
                    isDecodedModel.current = !!resp?.data?.model?.name;
                    isDecodedTrim.current = !!resp?.data?.trim?.name;

                    // Update trim engine value for display
                    if (resp?.data?.trim) {
                        setTrimEngineValue(resp?.data?.trim);
                    }
                }
            } catch (error) {
                console.error('VIN decode error:', error);
                toast.error('Failed to decode VIN');
            }
        } else if (getValues()?.vin && getValues()?.vin.length !== 17) {
            setError('vin', {
                message: 'Please enter valid VIN',
            });
        }
    };

    // License Decoding
    const handleDecodeLicense = async () => {
        if (!watchProvince) {
            setError('provinceId', {
                message: 'Please select province',
            });
            return;
        }

        if (watchProvince && debouncedLicenseValue) {
            const provinceCode = provinceCombinedList?.find((d) => d._id == watchProvince)?.provinceCode || 'ON';

            try {
                const resp = await decodeLicenseMutation.mutateAsync({
                    licenseNumber: debouncedLicenseValue,
                    provinceCode: provinceCode,
                });

                if (resp?.status) {
                    setValue('vin', resp?.data?.vin);
                    handleDecodeVIN();
                }
            } catch (error) {
                console.error('License decode error:', error);
                toast.error(error?.response?.data?.message || 'Failed to decode license plate');
            }
        }
    };

    // Make Creation
    const saveNewVehicleMake = async () => {
        if (!enableModelCreation) return;

        try {
            await createMakeMutation.mutateAsync({
                name: makeString,
                year: typeof watchYear === 'number' ? watchYear.toString() : watchYear,
            });
            toast.success('Make created successfully');
        } catch (error) {
            console.error('Make creation error:', error);
            toast.error(error?.response?.data?.message || 'Failed to create make');
        }
    };

    // Model Creation
    const saveNewVehicleModel = async () => {
        if (!enableModelCreation) return;

        try {
            await createModelMutation.mutateAsync({
                vehicleModelName: modelString,
                vehicleMakeId: watchMake,
                year: typeof watchYear === 'number' ? watchYear.toString() : watchYear,
            });
            toast.success('Model created successfully');
        } catch (error) {
            console.error('Model creation error:', error);
            toast.error('Failed to create model');
        }
    };

    // Trim Creation
    const saveNewVehicleTrim = async () => {
        if (!enableModelCreation) return;

        try {
            await createTrimMutation.mutateAsync({
                vehicleTrimName: trimString,
                vehicleModelId: watchModel,
                vehicleMakeId: watchMake,
                year: typeof watchYear === 'number' ? watchYear.toString() : watchYear,
            });
            toast.success('Trim created successfully');
        } catch (error) {
            console.error('Trim creation error:', error);
            toast.error('Failed to create trim');
        }
    };

    // Fleet Number Validation
    const isFleetNumberExists = async (inputValue) => {
        if (!isFleetCustomer || !inputValue) return false;

        // For local vehicles (create_from_page, update_from_page, and bulk modes)
        if ((mode === 'create_from_page' || mode === 'update_from_page' || mode === 'add') && localVehicles && setLocalVehicles) {
            // Get the current vehicle's index if editing
            const currentEditIndex = vehicleData?.editIndex;

            // Check if fleet number exists in other vehicles
            const isFleetUnitNumberAlreadyExists = localVehicles.find((vehicle, index) => {
                // Skip the current vehicle being edited
                if (currentEditIndex !== undefined && index === currentEditIndex) {
                    return false;
                }
                return vehicle?.fleetUnitNumber?.toLowerCase() === inputValue?.toLowerCase();
            });

            if (isFleetUnitNumberAlreadyExists) {
                setError('fleetUnitNumber', {
                    message: 'Fleet Unit Number already exists',
                });
                return true;
            } else {
                clearErrors('fleetUnitNumber');
                return false;
            }
        } else {
            // For API validation (other modes)
            let payload = { customerId: customerId, fleetUnitNumber: inputValue };
            if (vehicleId && vehicleId !== '') {
                payload.vehicleId = vehicleId;
            }
            const resp = await checkFleetNumberExists(payload);
            if (resp.exist) {
                setError('fleetUnitNumber', {
                    message: 'Fleet Unit Number already exists',
                });
                return true;
            } else {
                clearErrors('fleetUnitNumber');
                return false;
            }
        }
    };

    // Handle close
    const handleClose = () => {
        reset();
        setMakeString('');
        setModelString('');
        setTrimString('');
        setTrimEngineValue(null);
        setDecode({ vin: false, license: false });
        onClose();
    };

    // Submit form
    const handleFormSubmit = async (values) => {
        try {
            setIsLoading(true);

            // Validate VIN if provided
            if (values?.vin && values?.vin !== '') {
                if (checkValidVIN(values?.vin)) {
                    clearErrors('vin');
                } else {
                    setError('vin', { message: 'Please enter valid VIN' });
                    return;
                }
            }

            // Validate fleet number if provided
            if (isFleetCustomer && values?.fleetUnitNumber) {
                if (await isFleetNumberExists(values.fleetUnitNumber)) {
                    return;
                }
            }

            let currentYear = values?.year || watchYear;
            let currentMakeId = values?.vehicleMakeId;
            let currentModelId = values?.vehicleModelId;
            let currentTrimId = values?.vehicleModelTrimId;

            let createdMakeObj = null;
            let createdModelObj = null;
            let createdTrimObj = null;

            // Check if Make is selected and saved
            let isMakeSaved = currentMakeId && currentMakeId !== 'new' && vehicleMakeList?.some((m) => m._id === currentMakeId);

            // Auto-create Make if it was decoded from VIN/License plate and has not been saved yet
            if (!isMakeSaved && isDecodedMake.current && makeString?.trim()) {
                try {
                    const makeRes = await createMakeMutation.mutateAsync({
                        name: makeString.trim(),
                        year: typeof currentYear === 'number' ? currentYear.toString() : currentYear,
                    });
                    if (makeRes && makeRes._id) {
                        currentMakeId = makeRes._id;
                        createdMakeObj = makeRes;
                        setValue('vehicleMakeId', currentMakeId);
                        clearErrors('vehicleMakeId');
                        isMakeSaved = true;
                    }
                } catch (err) {
                    console.error('Auto create decoded make failed:', err);
                }
            }

            if (!isMakeSaved) {
                setError('vehicleMakeId', { message: 'Make is required' });
                setIsLoading(false);
                return;
            }

            // Check if Model is selected and saved
            let isModelSaved = currentModelId && currentModelId !== 'new' && vehicleModelList?.some((m) => m._id === currentModelId);

            // Auto-create Model if it was decoded from VIN/License plate and has not been saved yet
            if (!isModelSaved && isDecodedModel.current && modelString?.trim() && currentMakeId) {
                try {
                    const modelRes = await createModelMutation.mutateAsync({
                        vehicleModelName: modelString.trim(),
                        vehicleMakeId: currentMakeId,
                        year: typeof currentYear === 'number' ? currentYear.toString() : currentYear,
                    });
                    if (modelRes && modelRes._id) {
                        currentModelId = modelRes._id;
                        createdModelObj = modelRes;
                        setValue('vehicleModelId', currentModelId);
                        clearErrors('vehicleModelId');
                        isModelSaved = true;
                    }
                } catch (err) {
                    console.error('Auto create decoded model failed:', err);
                }
            }

            if (!isModelSaved) {
                setError('vehicleModelId', { message: 'Model is required' });
                setIsLoading(false);
                return;
            }

            let isTrimSaved = currentTrimId && currentTrimId !== 'new' && vehicleModelTrimList?.some((t) => t._id === currentTrimId);

            // Auto-create Trim if it was decoded from VIN/License plate and has not been saved yet
            if (!isTrimSaved && isDecodedTrim.current && trimString?.trim() && currentMakeId && currentModelId) {
                try {
                    const trimRes = await createTrimMutation.mutateAsync({
                        vehicleTrimName: trimString.trim(),
                        vehicleModelId: currentModelId,
                        vehicleMakeId: currentMakeId,
                        year: typeof currentYear === 'number' ? currentYear.toString() : currentYear,
                    });
                    if (trimRes && trimRes._id) {
                        currentTrimId = trimRes._id;
                        createdTrimObj = trimRes;
                        setValue('vehicleModelTrimId', currentTrimId);
                        clearErrors('vehicleModelTrimId');
                        isTrimSaved = true;
                    }
                } catch (err) {
                    console.error('Auto create decoded trim failed:', err);
                }
            }

            const validTrimId = currentTrimId && currentTrimId !== 'new' ? currentTrimId : undefined;

            // Create payload based on mode
            // For local modes (create_from_page, update_from_page, add), include full objects for UI display
            // For API modes, only send IDs
            const isLocalMode = mode === 'create_from_page' || mode === 'update_from_page' || mode === 'add';

            // Get full objects from lists for local storage
            const makeObject = vehicleMakeList?.find((m) => m._id === currentMakeId) || createdMakeObj || { _id: currentMakeId, name: makeString };
            const modelObject = vehicleModelList?.find((m) => m._id === currentModelId) || createdModelObj || { _id: currentModelId, name: modelString };
            const trimObject =
                vehicleModelTrimList?.find((t) => t._id === validTrimId) ||
                createdTrimObj ||
                (validTrimId ? { _id: validTrimId, name: trimString } : undefined);
            const provinceObject = provinceCombinedList?.find((p) => p._id === values?.provinceId);

            let payload = {
                year: values?.year?.toString(),
                // For local modes, store full objects; for API modes, store IDs
                vehicleMakeId: isLocalMode ? makeObject : currentMakeId,
                vehicleModelId: isLocalMode ? modelObject : currentModelId,
                vin: values?.vin || undefined,
                licenseNumber: values?.licenseNumber || '',
                provinceId: isLocalMode ? values?.provinceId : values?.provinceId,
                // Store province object for local modes to display province code
                ...(isLocalMode && provinceObject ? { provinceObject } : {}),
            };

            if (isFleetCustomer && values?.fleetUnitNumber) {
                payload.fleetUnitNumber = values?.fleetUnitNumber;
            }

            if (validTrimId) {
                // For local modes, store full trim object (includes description)
                payload.vehicleModelTrimId = isLocalMode ? trimObject : validTrimId;
            }

            // Add customer ID if provided
            if (customerId) {
                payload.customerId = customerId;
            }

            // Handle different modes
            switch (mode) {
                case USER_TYPES.SHOP_IMPORTED:
                    // Shop imported vehicle
                    if (vehicleData?._id) {
                        await createImportedVehicleMutation.mutateAsync({ vehicleId: vehicleData?._id, vehicleData: payload });
                    } else {
                        toast.error('Vehicle ID is required for update');
                    }
                    break;
                case 'update_shop_imported_vehicle':
                    if (vehicleData) {
                        await updateImportedVehicleMutation.mutateAsync({ vehicleId: vehicleData?._id, vehicleData: payload });
                    } else {
                        toast.error('Vehicle ID is required for update');
                    }
                    break;
                case 'edit':
                case 'update_shop_created_vehicle':
                    // Update existing vehicle
                    console.log('🚀 ~ handleFormSubmit ~ vehicleId:', vehicleId);
                    if (vehicleId) {
                        await updateVehicleMutation.mutateAsync({
                            vehicleId: vehicleId,
                            vehicleData: payload,
                        });
                    } else {
                        toast.error('Vehicle ID is required for update');
                    }
                    break;

                case 'create':
                default:
                    // Create new vehicle
                    await createVehicleMutation.mutateAsync(payload);
                    break;

                case 'create_from_page':
                    // Add new vehicle to local array (for NewCustomers page)
                    if (localVehicles && setLocalVehicles) {
                        setLocalVehicles([...localVehicles, payload]);
                        handleClose();
                    }
                    break;

                case 'update_from_page':
                    // Update existing vehicle in local array (for NewCustomers page)
                    if (localVehicles && setLocalVehicles && vehicleData?.editIndex !== undefined) {
                        const updatedVehicles = [...localVehicles];
                        updatedVehicles[vehicleData.editIndex] = payload;
                        setLocalVehicles(updatedVehicles);
                        handleClose();
                    }
                    break;

                case 'bulk':
                    // Handle bulk operations
                    if (localVehicles && setLocalVehicles) {
                        if (vehicleData) {
                            // Edit existing vehicle in local array
                            const updatedVehicles = [...localVehicles];
                            const index = updatedVehicles.findIndex((v) => v === vehicleData);
                            if (index !== -1) {
                                updatedVehicles[index] = payload;
                                setLocalVehicles(updatedVehicles);
                            }
                        } else {
                            // Add new vehicle to local array
                            setLocalVehicles([...localVehicles, payload]);
                        }
                        handleClose();
                    }
                    break;

                case 'confirm':
                    // Handle confirmation mode
                    if (onSubmit) {
                        await onSubmit(payload);
                    }
                    break;
            }

            // Call custom submit handler if provided and not handled above
            if (onSubmit && !['edit', 'update_shop_created_vehicle', 'create', 'bulk', 'confirm'].includes(mode)) {
                await onSubmit(payload);
            }
        } catch (error) {
            console.error('Error submitting vehicle:', error);
            toast.error('Error saving vehicle');
        } finally {
            setIsLoading(false);
        }
    };

    // Set default province if not set
    // Set default province - runs AFTER form reset for create modes
    useEffect(() => {
        if (open && provinceCombinedList?.length > 0) {
            const defaultProvince = provinceCombinedList?.find((d) => d.isDefault === true)?._id;

            // For create modes, set default province after a small delay to ensure form is reset first
            if (mode === 'create_from_page' || mode === 'create' || mode === 'add') {
                setTimeout(() => {
                    setValue('provinceId', defaultProvince);
                }, 100);
            } else if (!getValues('provinceId')) {
                // For other modes, only set if not already set
                setValue('provinceId', defaultProvince);
            }
        }
    }, [open, provinceCombinedList, mode]);

    // Initialize form with vehicle data for edit mode
    useEffect(() => {
        if (
            open &&
            vehicleData &&
            (mode === 'edit' ||
                mode === 'update_shop_created_vehicle' ||
                mode === 'update_from_page' ||
                mode === USER_TYPES.SHOP_IMPORTED ||
                mode === 'update_shop_imported_vehicle')
        ) {
            console.log('🚀 ~ useEffect ~ vehicleData:', vehicleData);
            setValue('vin', vehicleData?.vin || '');
            setValue('year', vehicleData?.year || '');
            setyearHookValue(vehicleData?.year || null);
            setValue('vehicleMakeId', vehicleData?.vehicleMakeId || '');
            setMakeHookValue(vehicleData?.vehicleMakeId || null);
            setValue('vehicleModelId', vehicleData?.vehicleModelId || '');
            setValue('vehicleModelTrimId', vehicleData?.vehicleModelTrimId || '');
            setValue('licenseNumber', vehicleData?.licenseNumber || '');
            setValue('provinceId', vehicleData?.provinceId || '');
            setValue('fleetUnitNumber', vehicleData?.fleetUnitNumber || '');

            if (vehicleData?.vehicleMakeId?.name) {
                setMakeString(vehicleData?.vehicleMakeId?.name || '');
            }
            if (vehicleData?.vehicleModelId?.name) {
                setModelString(vehicleData?.vehicleModelId?.name || '');
            }
            if (vehicleData?.vehicleModelTrimId?.name) {
                setTrimString(vehicleData?.vehicleModelTrimId?.name || '');
                setTrimEngineValue(vehicleData?.vehicleModelTrimId);
            }
        } else if (open && (mode === 'create_from_page' || mode === 'create' || mode === 'add')) {
            // Reset form for create modes
            reset();
            setMakeString('');
            setModelString('');
            setTrimString('');
            setTrimEngineValue(null);
            setMakeHookValue(null);
            setyearHookValue(null);
        }
    }, [open, vehicleData, mode]);

    // Effects
    useEffect(() => {
        if (debouncedVinValue && debouncedVinValue.length === 17 && shouldDecodeVIN.current) {
            shouldDecodeVIN.current = false; // Reset flag immediately
            handleDecodeVIN();
        }
    }, [debouncedVinValue]);

    useEffect(() => {
        if (config?.config?.isLicensePlateDecodeEnabled && debouncedLicenseValue && shouldDecodeLicense.current) {
            shouldDecodeLicense.current = false; // Reset flag immediately
            handleDecodeLicense();
        }
    }, [debouncedLicenseValue]);
    // Dialog header
    const DialogHeader = ({ dialogTitle, disabled, onClose }) => {
        return (
            <DialogTitle sx={STYLES.dialogHeaderStyles}>
                <Typography variant="h6" width={'98%'}>
                    {dialogTitle}
                </Typography>
                <Stack direction={'row'} spacing={0}>
                    <div
                        onClick={() => {
                            if (!disabled) {
                                onClose();
                            }
                        }}
                        style={{ cursor: 'pointer', height: '32px' }}
                    >
                        <Close style={{ fontSize: 32 }} />
                    </div>
                </Stack>
            </DialogTitle>
        );
    };

    // Dialog footer
    const DialogFooter = ({ disabled = false, onCancel, onSubmit }) => {
        return (
            <DialogActions sx={{ padding: '0!important' }}>
                <Grid item container gap={2} justifyContent={'flex-end'} padding={'16px'} borderTop={'1px solid rgba(0, 0, 0, 0.12)'}>
                    <Grid item>
                        <Stack spacing={2} direction={'row'}>
                            <Button variant="outlined" color="primary" disabled={disabled} onClick={onCancel}>
                                CANCEL
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid item>
                        <Stack spacing={2} direction={'row'}>
                            <Button variant="contained" color="primary" onClick={onSubmit} disabled={disabled}>
                                SAVE
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </DialogActions>
        );
    };

    const getDialogTitle = () => {
        switch (mode) {
            case USER_TYPES.SHOP_IMPORTED:
                return 'Confirm Vehicle Information';
            case 'edit':
            case 'update_shop_created_vehicle':
            case 'update_from_page':
            case 'update_shop_imported_vehicle':
                return 'Edit Vehicle';
            case 'confirm':
                return 'Confirm Vehicle Information';
            default:
                return 'Add Vehicle';
        }
    };
    return (
        <>
            <Dialog
                className="commonDialog__720"
                open={open}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleClose();
                    }
                }}
                disableBackdropClick
                maxWidth="lg"
                fullWidth
            >
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <DialogHeader
                        dialogTitle={getDialogTitle()}
                        disabled={
                            createMakeMutation.isPending ||
                            createModelMutation.isPending ||
                            createTrimMutation.isPending ||
                            createVehicleMutation.isPending ||
                            updateVehicleMutation.isPending ||
                            decodeVINMutation.isPending ||
                            decodeLicenseMutation.isPending ||
                            createImportedVehicleMutation.isPending
                        }
                        onClose={handleClose}
                    />

                    <DialogContent sx={STYLES.dialogContentStyle}>
                        <Grid container spacing={3}>
                            {showMessage && Object.keys(errors).length > 0 && (
                                <Grid item xs={12}>
                                    <Typography style={{ color: REQUIRED_RED }}>Please fill out the missing fields for this vehicle.</Typography>
                                </Grid>
                            )}

                            {/* VIN  */}
                            <Grid item xs={12} md={12} xl={12}>
                                <Typography variant="body1" className="label-500">
                                    VIN
                                </Typography>
                                <Controller
                                    name="vin"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            inputProps={{
                                                maxLength: 17,
                                            }}
                                            error={!!errors.vin}
                                            helperText={errors.vin ? errors.vin.message : ''}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                setDecode({ ...decode, vin: true });
                                                setValue('vin', e.target.value?.toUpperCase());
                                                shouldDecodeVIN.current = true; // Set flag to trigger decode
                                                if (e.target.value !== '') {
                                                    const regex = checkValidVIN(e.target.value);
                                                    if (regex) {
                                                        clearErrors('vin');
                                                    } else {
                                                        setError('vin', {
                                                            message: 'Please enter valid VIN',
                                                        });
                                                    }
                                                } else {
                                                    clearErrors('vin');
                                                }
                                            }}
                                            InputProps={{
                                                endAdornment: decodeVINMutation.isPending ? <CircularProgress size={20} /> : null,
                                            }}
                                            sx={MuiTextFieldWithFocus}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Divider  */}
                            <Divider textAlign="center" style={STYLES.divider}>
                                or
                            </Divider>

                            {/* Year  */}
                            <Grid item xs={12} md={4} xl={4}>
                                <Typography variant="body1" className="label-500">
                                    Year <span style={{ color: '#D32F2F' }}> *</span>
                                </Typography>

                                <Controller
                                    name="year"
                                    control={control}
                                    rules={{
                                        required: 'Year is required',
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            fullWidth
                                            value={yearHookValue}
                                            onChange={(event) => {
                                                field.onChange(event);
                                                setyearHookValue(event.target.value);
                                                // Clear all dependent fields
                                                setValue('vehicleMakeId', '');
                                                setValue('vehicleModelId', '');
                                                setValue('vehicleModelTrimId', '');
                                                setMakeHookValue(null);
                                                setMakeString('');
                                                setModelString('');
                                                setTrimString('');
                                            }}
                                            error={!!errors.year}
                                            MenuProps={{
                                                style: { maxHeight: '400px' },
                                            }}
                                            sx={MuiSelectWithFocus}
                                        >
                                            {vehicleYearListLoader ? (
                                                <MenuItem disabled>
                                                    <CircularProgress size={24} />
                                                </MenuItem>
                                            ) : (
                                                Array.isArray(vehicleYearList) &&
                                                vehicleYearList.map((y) => (
                                                    <MenuItem value={y} key={y}>
                                                        {y}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    )}
                                />
                                {errors.year && (
                                    <Typography color="error" variant="caption" display="block">
                                        {errors.year.message}
                                    </Typography>
                                )}
                            </Grid>

                            {/* Make  */}
                            <Grid item xs={12} md={4} xl={4}>
                                <Typography variant="body1" className="label-500">
                                    Make <span style={{ color: '#D32F2F' }}> *</span>
                                </Typography>
                                <Controller
                                    name="vehicleMakeId"
                                    control={control}
                                    rules={{
                                        required: 'Make is required',
                                    }}
                                    render={({ field }) => (
                                        <>
                                            <Autocomplete
                                                fullWidth
                                                freeSolo
                                                disableClearable
                                                forcePopupIcon
                                                disabled={!watchYear}
                                                loading={vehicleMakeListLoader || createMakeMutation.isPending}
                                                options={
                                                    vehicleMakeList?.length > 0
                                                        ? !makeString ||
                                                          vehicleMakeList.some((item) => item?.name?.toLowerCase() === makeString?.trim()?.toLowerCase())
                                                            ? vehicleMakeList
                                                            : [{ _id: 'new', name: makeString }, ...vehicleMakeList]
                                                        : makeString
                                                          ? [{ _id: 'new', name: makeString }]
                                                          : []
                                                }
                                                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                                value={vehicleMakeList?.length > 0 ? vehicleMakeList.find((m) => m._id === field.value) || null : null}
                                                onBlur={() => {
                                                    if (!field.value) {
                                                        setMakeString('');
                                                    } else {
                                                        const selected = vehicleMakeList?.find((m) => m._id === field.value);
                                                        if (selected) setMakeString(selected.name);
                                                    }
                                                }}
                                                onChange={async (event, newValue) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    let selectedMake = newValue;

                                                    if (selectedMake && selectedMake._id && selectedMake._id !== 'new') {
                                                        clearErrors('vehicleMakeId');
                                                        field.onChange(selectedMake._id);
                                                        setMakeHookValue(selectedMake._id);
                                                        setMakeString(selectedMake.name);
                                                        // Clear dependent fields
                                                        setValue('vehicleModelId', '');
                                                        setValue('vehicleModelTrimId', '');
                                                        setModelString('');
                                                        setTrimString('');
                                                    }
                                                }}
                                                onHighlightChange={(event, option) => {
                                                    highlightedMake.current = option;
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                        event.preventDefault();
                                                        event.stopPropagation();

                                                        // An option is highlighted in the list - take it over anything the text implies
                                                        const highlighted = highlightedMake.current;
                                                        if (highlighted && highlighted._id && highlighted._id !== 'new') {
                                                            clearErrors('vehicleMakeId');
                                                            field.onChange(highlighted._id);
                                                            setMakeHookValue(highlighted._id);
                                                            setMakeString(highlighted.name);
                                                            setValue('vehicleModelId', '');
                                                            setValue('vehicleModelTrimId', '');
                                                            setModelString('');
                                                            setTrimString('');
                                                            highlightedMake.current = null;
                                                            setTimeout(() => document.activeElement.blur(), 0);
                                                            return;
                                                        }

                                                        const inputLowerCase = makeString?.trim()?.toLowerCase();
                                                        const exactMatch = vehicleMakeList?.find((option) => option.name.toLowerCase() === inputLowerCase);

                                                        if (exactMatch) {
                                                            field.onChange(exactMatch._id);
                                                            setMakeHookValue(exactMatch._id);
                                                            setMakeString(exactMatch.name);
                                                            setValue('vehicleModelId', '');
                                                            setValue('vehicleModelTrimId', '');
                                                            setModelString('');
                                                            setTrimString('');
                                                            setTimeout(() => document.activeElement.blur(), 0);
                                                        } else if (inputLowerCase && enableModelCreation) {
                                                            saveNewVehicleMake();
                                                        }
                                                    }
                                                }}
                                                onInputChange={(event, newValue, reason) => {
                                                    setMakeString(newValue);
                                                    if (reason === 'input') {
                                                        isDecodedMake.current = false;
                                                        highlightedMake.current = null;
                                                    }
                                                    if (!newValue) {
                                                        field.onChange(null);
                                                    }
                                                }}
                                                inputValue={makeString}
                                                renderOption={(props, option) => {
                                                    if (option._id === 'new') {
                                                        return (
                                                            <li
                                                                {...props}
                                                                key={`new-make-${option.name}`}
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    height: '44px',
                                                                    paddingRight: '0',
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        flex: 1,
                                                                        minWidth: 0,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        marginRight: '8px',
                                                                    }}
                                                                >
                                                                    {option.name}
                                                                </span>
                                                                <Button
                                                                    size="small"
                                                                    color="primary"
                                                                    style={{
                                                                        background: '#2196F326',
                                                                        flexShrink: 0,
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        e.preventDefault();
                                                                        saveNewVehicleMake();
                                                                    }}
                                                                >
                                                                    {createMakeMutation.isPending ? <CircularProgress disableShrink size={18} /> : 'Save'}
                                                                </Button>
                                                            </li>
                                                        );
                                                    }
                                                    return (
                                                        <li {...props} key={option._id}>
                                                            {option.name}
                                                        </li>
                                                    );
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={!!errors.vehicleMakeId}
                                                        helperText={errors.vehicleMakeId?.message || ''}
                                                        disabled={!watchYear}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            endAdornment: (
                                                                <>
                                                                    {(vehicleMakeListLoader || createMakeMutation.isPending) && watchYear ? (
                                                                        <CircularProgress style={STYLES.loaderSmall} />
                                                                    ) : null}
                                                                    {params.InputProps.endAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                        sx={!watchYear ? MuiDisabledTextField : MuiTextFieldWithFocus}
                                                    />
                                                )}
                                                PaperComponent={(props) => <CustomPaper {...props} />}
                                            />
                                        </>
                                    )}
                                />
                                {vehicleMakeList?.find((m) => m._id === watchMake)?.requestStatus === 'in-review' && (
                                    <Typography
                                        sx={{
                                            marginTop: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontStyle: 'italic',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            letterSpacing: '0.15px',
                                            lineHeight: '120%',
                                        }}
                                    >
                                        <Info color="#006CDD" sx={{ fontSize: 20, color: '#006CDD' }} /> awaiting Apex Team approval
                                    </Typography>
                                )}
                            </Grid>

                            {/* Model  */}
                            <Grid item xs={12} md={4} xl={4}>
                                <Typography variant="body1" className="label-500">
                                    Model <span style={{ color: '#D32F2F' }}> *</span>
                                </Typography>
                                <Controller
                                    name="vehicleModelId"
                                    control={control}
                                    rules={{ required: 'Model is required' }}
                                    render={({ field, fieldState }) => (
                                        <Autocomplete
                                            fullWidth
                                            freeSolo
                                            disableClearable
                                            forcePopupIcon
                                            disabled={!watchYear || !watchMake}
                                            loading={vehicleModelListLoader || createModelMutation.isPending}
                                            options={
                                                vehicleModelList?.length > 0
                                                    ? !modelString ||
                                                      vehicleModelList.some((item) => item?.name?.toLowerCase() === modelString?.trim()?.toLowerCase())
                                                        ? vehicleModelList
                                                        : [{ _id: 'new', name: modelString }, ...vehicleModelList]
                                                    : modelString
                                                      ? [{ _id: 'new', name: modelString }]
                                                      : []
                                            }
                                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                                            isOptionEqualToValue={(option, value) => {
                                                return option._id === value._id;
                                            }}
                                            value={vehicleModelList?.length > 0 ? vehicleModelList.find((m) => m._id === field.value) || null : null}
                                            onBlur={() => {
                                                if (!field.value) {
                                                    setModelString('');
                                                } else {
                                                    const selected = vehicleModelList?.find((m) => m._id === field.value);
                                                    if (selected) setModelString(selected.name);
                                                }
                                            }}
                                            onChange={async (event, newValue) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                let selectedModel = newValue;

                                                if (selectedModel && selectedModel._id && selectedModel._id !== 'new') {
                                                    clearErrors('vehicleModelId');
                                                    field.onChange(selectedModel._id);
                                                    setValue('vehicleModelTrimId', '');
                                                    setTrimString('');
                                                }
                                            }}
                                            onHighlightChange={(event, option) => {
                                                highlightedModel.current = option;
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    event.stopPropagation();

                                                    // An option is highlighted in the list - take it over anything the text implies
                                                    const highlighted = highlightedModel.current;
                                                    if (highlighted && highlighted._id && highlighted._id !== 'new') {
                                                        clearErrors('vehicleModelId');
                                                        field.onChange(highlighted._id);
                                                        setModelString(highlighted.name);
                                                        setValue('vehicleModelTrimId', '');
                                                        setTrimString('');
                                                        highlightedModel.current = null;
                                                        setTimeout(() => document.activeElement.blur(), 0);
                                                        return;
                                                    }

                                                    const inputLowerCase = modelString?.trim()?.toLowerCase();
                                                    const exactMatch = vehicleModelList?.find((option) => option.name.toLowerCase() === inputLowerCase);

                                                    if (exactMatch) {
                                                        field.onChange(exactMatch._id);
                                                        setModelString(exactMatch.name);
                                                        setValue('vehicleModelTrimId', '');
                                                        setTrimString('');
                                                        setTimeout(() => document.activeElement.blur(), 0);
                                                    } else if (inputLowerCase && enableModelCreation) {
                                                        saveNewVehicleModel();
                                                    }
                                                }
                                            }}
                                            onInputChange={(event, newValue, reason) => {
                                                setModelString(newValue);
                                                if (reason === 'input') {
                                                    isDecodedModel.current = false;
                                                    highlightedModel.current = null;
                                                }
                                                if (!newValue) field.onChange(null);
                                            }}
                                            inputValue={modelString}
                                            renderOption={(props, option) => {
                                                if (option._id === 'new') {
                                                    return (
                                                        <li
                                                            {...props}
                                                            key={`new-model-${option.name}`}
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                height: '44px',
                                                                paddingRight: '0',
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    marginRight: '8px',
                                                                }}
                                                            >
                                                                {option.name}
                                                            </span>
                                                            <Button
                                                                size="small"
                                                                color="primary"
                                                                style={{
                                                                    background: '#2196F326',
                                                                    flexShrink: 0,
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    e.preventDefault();
                                                                    saveNewVehicleModel();
                                                                }}
                                                            >
                                                                {createModelMutation.isPending ? <CircularProgress disableShrink size={18} /> : 'Save'}
                                                            </Button>
                                                        </li>
                                                    );
                                                }
                                                return (
                                                    <li {...props} key={option._id}>
                                                        {option.name}
                                                    </li>
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={!!errors.vehicleModelId}
                                                    helperText={errors.vehicleModelId ? errors.vehicleModelId.message : ''}
                                                    disabled={!watchYear || !watchMake}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {(vehicleModelListLoader || createModelMutation.isPending) && watchYear && watchMake ? (
                                                                    <CircularProgress style={STYLES.loaderSmall} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    sx={!watchYear || !watchMake ? MuiDisabledTextField : MuiTextFieldWithFocus}
                                                />
                                            )}
                                            PaperComponent={(props) => <CustomPaper {...props} />}
                                        />
                                    )}
                                />
                                {vehicleModelList?.find((m) => m._id === watchModel)?.requestStatus === 'in-review' && (
                                    <Typography
                                        sx={{
                                            marginTop: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontStyle: 'italic',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            letterSpacing: '0.15px',
                                            lineHeight: '120%',
                                        }}
                                    >
                                        <Info color="#006CDD" sx={{ fontSize: 20, color: '#006CDD' }} /> awaiting Apex Team approval
                                    </Typography>
                                )}
                            </Grid>

                            {/* Trim  */}
                            <Grid item xs={12} md={12} xl={12}>
                                <Typography variant="body1" className="label-500">
                                    Trim
                                </Typography>
                                <Controller
                                    name="vehicleModelTrimId"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <Autocomplete
                                            fullWidth
                                            freeSolo
                                            disableClearable
                                            forcePopupIcon
                                            loading={vehicleModelTrimListLoader || createTrimMutation.isPending}
                                            disabled={!watchYear || !watchMake || !watchModel}
                                            options={
                                                vehicleModelTrimList?.length > 0
                                                    ? !trimString ||
                                                      vehicleModelTrimList.some((item) => item?.name?.toLowerCase() === trimString?.trim()?.toLowerCase())
                                                        ? vehicleModelTrimList
                                                        : [{ _id: 'new', description: trimString, name: trimString }, ...vehicleModelTrimList]
                                                    : trimString
                                                      ? [{ _id: 'new', description: trimString, name: trimString }]
                                                      : []
                                            }
                                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                                            isOptionEqualToValue={(option, value) => {
                                                return option._id === value._id;
                                            }}
                                            onBlur={() => {
                                                if (!field.value) {
                                                    setTrimString('');
                                                } else {
                                                    const selected = vehicleModelTrimList?.find((m) => m._id === field.value);
                                                    if (selected) setTrimString(selected.name);
                                                }
                                            }}
                                            value={vehicleModelTrimList?.length > 0 ? vehicleModelTrimList.find((m) => m._id === field.value) || null : null}
                                            onChange={async (event, newValue) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                let selectTrim = newValue;

                                                if (selectTrim && selectTrim._id && selectTrim._id !== 'new') {
                                                    clearErrors('vehicleModelTrimId');
                                                    field.onChange(selectTrim._id);
                                                    setTrimEngineValue(vehicleModelTrimList?.find((d) => d?._id == selectTrim._id));
                                                }
                                            }}
                                            onHighlightChange={(event, option) => {
                                                highlightedTrim.current = option;
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    event.stopPropagation();

                                                    // An option is highlighted in the list - take it over anything the text implies
                                                    const highlighted = highlightedTrim.current;
                                                    if (highlighted && highlighted._id && highlighted._id !== 'new') {
                                                        clearErrors('vehicleModelTrimId');
                                                        field.onChange(highlighted._id);
                                                        setTrimString(highlighted.name);
                                                        setTrimEngineValue(highlighted);
                                                        highlightedTrim.current = null;
                                                        setTimeout(() => document.activeElement.blur(), 0);
                                                        return;
                                                    }

                                                    const inputLowerCase = trimString?.trim()?.toLowerCase();
                                                    const exactMatch = vehicleModelTrimList?.find((option) => option.name.toLowerCase() === inputLowerCase);

                                                    if (exactMatch) {
                                                        field.onChange(exactMatch._id);
                                                        setTrimString(exactMatch.name);
                                                        setTrimEngineValue(exactMatch);
                                                        setTimeout(() => document.activeElement.blur(), 0);
                                                    } else if (inputLowerCase && enableModelCreation) {
                                                        saveNewVehicleTrim();
                                                    }
                                                }
                                            }}
                                            onInputChange={(event, newValue, reason) => {
                                                setTrimString(newValue);
                                                if (reason === 'input') {
                                                    isDecodedTrim.current = false;
                                                    highlightedTrim.current = null;
                                                }
                                                if (!newValue) field.onChange(null);
                                            }}
                                            renderOption={(props, option) => {
                                                if (option._id === 'new') {
                                                    return (
                                                        <li
                                                            {...props}
                                                            key={`new-trim-${option.name}`}
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                height: '44px',
                                                                paddingRight: '0',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontWeight: 500,
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    marginRight: '8px',
                                                                }}
                                                            >
                                                                {option.name}
                                                            </div>
                                                            <Button
                                                                size="small"
                                                                color="primary"
                                                                style={{
                                                                    background: '#2196F326',
                                                                    flexShrink: 0,
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    e.preventDefault();
                                                                    saveNewVehicleTrim();
                                                                }}
                                                            >
                                                                {createTrimMutation.isPending ? <CircularProgress disableShrink size={18} /> : 'Save'}
                                                            </Button>
                                                        </li>
                                                    );
                                                }
                                                return (
                                                    <Box
                                                        component="li"
                                                        {...props}
                                                        key={option._id}
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start!important',
                                                            justifyContent: 'start!important',
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 500 }}>{option?.name}</div>
                                                        {option?.description && <div>{option.description}</div>}
                                                    </Box>
                                                );
                                            }}
                                            PaperComponent={(props) => <CustomPaper {...props} />}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={!!errors.vehicleModelTrimId}
                                                    helperText={errors.vehicleModelTrimId ? errors.vehicleModelTrimId.message : ''}
                                                    disabled={!watchYear || !watchMake || !watchModel}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {(vehicleModelTrimListLoader || createTrimMutation.isPending) &&
                                                                watchYear &&
                                                                watchMake &&
                                                                watchModel ? (
                                                                    <CircularProgress style={STYLES.loaderSmall} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    sx={!watchYear || !watchMake || !watchModel ? MuiDisabledTextField : MuiTextFieldWithFocus}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Divider  */}
                            <Divider textAlign="center" style={STYLES.divider}>
                                and
                            </Divider>

                            {/* License Plate  */}
                            <LicensePlateInputV4
                                control={control}
                                errors={errors}
                                config={config?.config}
                                provinces={provinceCombinedList}
                                provinceValue={getValues('provinceId')}
                                setProvinceValue={setValue}
                                provinceLoader={provinceCombinedListLoader}
                                isFleetCustomer={isFleetCustomer}
                                getValues={getValues}
                                setValueInForm={setValue}
                                clearErrors={clearErrors}
                                setDecode={setDecode}
                                decode={decode}
                                handleDecodeLicense={handleDecodeLicense}
                                decodeLicenseLoader={decodeLicenseMutation.isPending}
                                shouldDecodeLicense={shouldDecodeLicense}
                            />

                            {/* Fleet Unit  */}
                            {isFleetCustomer && (
                                <Grid item xs={12} md={isFleetCustomer ? 6 : 12}>
                                    <Typography variant="body1" className="label-500">
                                        Fleet Unit #
                                    </Typography>
                                    <Controller
                                        name="fleetUnitNumber"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                error={!!errors.fleetUnitNumber}
                                                helperText={errors.fleetUnitNumber ? errors.fleetUnitNumber.message : ''}
                                                onChange={(e) => {
                                                    const inputValue = e.target.value;
                                                    field.onChange(inputValue);

                                                    if (debounceRef.current) clearTimeout(debounceRef.current);

                                                    if (inputValue) {
                                                        debounceRef.current = setTimeout(async () => {
                                                            isFleetNumberExists(inputValue);
                                                        }, 400);
                                                    }
                                                }}
                                                sx={MuiTextFieldWithFocus}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}

                            {/* Engine Details */}
                            {showEngineDetails &&
                                trimEngineValue &&
                                (trimEngineValue?.bodyType ||
                                    trimEngineValue?.engineDriveType ||
                                    trimEngineValue?.engineFuelType ||
                                    trimEngineValue?.engineTransmission ||
                                    trimEngineValue?.engineType) && (
                                    <>
                                        <Divider
                                            textAlign="center"
                                            style={{
                                                width: '100%',
                                                paddingLeft: '24px',
                                                marginTop: '16px',
                                            }}
                                        />
                                        <Grid
                                            container
                                            xs={12}
                                            md={12}
                                            xl={12}
                                            spacing={3}
                                            style={{
                                                marginLeft: '24px',
                                                marginTop: '24px',
                                                padding: 0,
                                                background: '#F5F5F7',
                                            }}
                                            borderRadius={2}
                                        >
                                            {trimEngineValue?.engineType && (
                                                <Grid item xs={12} md={6} style={{ padding: '16px 0px 0px 16px' }}>
                                                    <Typography fontWeight={500} fontSize={'16px'}>
                                                        Engine Type
                                                    </Typography>
                                                    <Typography fontWeight={400} fontSize={'16px'} color={'rgba(0, 0, 0, 0.6)'}>
                                                        {trimEngineValue?.engineType}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {trimEngineValue?.engineFuelType && (
                                                <Grid item xs={12} md={6} style={{ padding: '16px 0px 0px 16px' }}>
                                                    <Typography fontWeight={500} fontSize={'16px'}>
                                                        Engine Fuel Type
                                                    </Typography>
                                                    <Typography fontWeight={400} fontSize={'16px'} color={'rgba(0, 0, 0, 0.6)'}>
                                                        {trimEngineValue?.engineFuelType}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {trimEngineValue?.engineDriveType && (
                                                <Grid item xs={12} md={6} style={{ padding: '16px 0px 0px 16px' }}>
                                                    <Typography fontWeight={500} fontSize={'16px'}>
                                                        Engine Drive Type
                                                    </Typography>
                                                    <Typography fontWeight={400} fontSize={'16px'} color={'rgba(0, 0, 0, 0.6)'}>
                                                        {trimEngineValue?.engineDriveType}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {trimEngineValue?.engineTransmission && (
                                                <Grid item xs={12} md={6} style={{ padding: '16px 0px 0px 16px' }}>
                                                    <Typography fontWeight={500} fontSize={'16px'}>
                                                        Engine Transmission
                                                    </Typography>
                                                    <Typography fontWeight={400} fontSize={'16px'} color={'rgba(0, 0, 0, 0.6)'}>
                                                        {trimEngineValue?.engineTransmission}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {trimEngineValue?.bodyType && (
                                                <Grid item xs={12} md={6} style={{ padding: '16px 0px 16px 16px' }}>
                                                    <Typography fontWeight={500} fontSize={'16px'}>
                                                        Body Type
                                                    </Typography>
                                                    <Typography fontWeight={400} fontSize={'16px'} color={'rgba(0, 0, 0, 0.6)'}>
                                                        {trimEngineValue?.bodyType}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </>
                                )}
                        </Grid>
                    </DialogContent>

                    <DialogFooter
                        onCancel={handleClose}
                        onSubmit={handleSubmit(handleFormSubmit)}
                        disabled={
                            isLoading ||
                            createModelMutation.isPending ||
                            createTrimMutation.isPending ||
                            createVehicleMutation.isPending ||
                            updateVehicleMutation.isPending ||
                            decodeVINMutation.isPending ||
                            decodeLicenseMutation.isPending ||
                            createImportedVehicleMutation.isPending
                        }
                    />
                </form>
            </Dialog>
        </>
    );
};

export default VehicleModalV4;
